import React, { useState, useEffect, useCallback } from "react";
import MicroWellNavigationTabs from "../Models/MicroWell/NavigationTabs";
import { Box, Chip, CircularProgress, Tooltip, IconButton, Snackbar, Alert, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_BASE_URL } from "../../utils/constants";

const HEALTH_URL = (API_BASE_URL || "").replace(/\/+$/, "") + "/health";
const RE_WARM_INTERVAL_MS = 4 * 60 * 1000;
const WARMUP_TIMEOUT_MS = 20 * 1000;

function MicroWell() {
    const [status, setStatus] = useState("warming");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [lastReadyAt, setLastReadyAt] = useState(null);

    const warmupBackend = useCallback(async (showSnackbar = false) => {
        setStatus("warming");
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), WARMUP_TIMEOUT_MS);
        try {
            await fetch(HEALTH_URL, { method: "GET", signal: controller.signal });
            setStatus("ready");
            setLastReadyAt(new Date());
            if (showSnackbar) setSnackbarOpen(true);
        } catch (err) {
            const msg = String(err?.message || "");
            const reallyOffline = err?.name === "AbortError"
                || /Failed to fetch|NetworkError|ECONNREFUSED/i.test(msg);
            if (reallyOffline) {
                setStatus("error");
            } else {
                setStatus("ready");
                setLastReadyAt(new Date());
                if (showSnackbar) setSnackbarOpen(true);
            }
        } finally {
            clearTimeout(t);
        }
    }, []);

    useEffect(() => {
        warmupBackend(true);
        const interval = setInterval(() => warmupBackend(false), RE_WARM_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [warmupBackend]);

    const statusChip = (() => {
        if (status === "warming") {
            return (
                <Tooltip title="Pinging /health to wake the AWS Lambda. Container-image Lambdas can take 5-15s to cold-start.">
                    <Chip
                        size="small"
                        color="warning"
                        icon={<CircularProgress size={14} sx={{ color: "inherit" }} />}
                        label="Warming backend..."
                    />
                </Tooltip>
            );
        }
        if (status === "ready") {
            const stamp = lastReadyAt ? ` (${lastReadyAt.toLocaleTimeString()})` : "";
            return (
                <Tooltip title={`Lambda responded to /health${stamp}. Calls should return in ~200 ms. Auto re-warms every 4 minutes.`}>
                    <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon fontSize="small" />}
                        label={`Backend ready${stamp}`}
                    />
                </Tooltip>
            );
        }
        return (
            <Tooltip title="Lambda did not respond to /health within 20s. Calls will still retry, but the first one may take a while. Click the refresh icon to try again.">
                <Chip
                    size="small"
                    color="error"
                    icon={<ErrorIcon fontSize="small" />}
                    label="Backend offline / cold"
                />
            </Tooltip>
        );
    })();

    return (
        <Box sx={{ p: 0 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 2,
                    pt: 1
                }}
            >
                <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", fontFamily: "monospace" }}>
                    API: {API_BASE_URL || "(not configured)"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {statusChip}
                    <Tooltip title="Re-ping /health">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => warmupBackend(true)}
                                disabled={status === "warming"}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <MicroWellNavigationTabs />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={() => setSnackbarOpen(false)}
                    sx={{ width: "100%" }}
                >
                    Backend ready � safe to run Calibration / Prediction
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MicroWell;



